/**
 * Detailed status panel for one selected manual ingestion job.
 */

import type { JobStatusResponse } from "../../api/clinicalTrialsApiTypes";
import {
  formatJobError,
  formatJobDuration,
  formatJobSource,
  formatJobTimestamp,
} from "./jobFormatters";
import { JobBatchDetails } from "./JobBatchDetails";
import { JobOutcomeSummary } from "./JobOutcomeSummary";
import { JobStatusBadge } from "./JobStatusBadge";

interface JobDetailsProps {
  job: JobStatusResponse | null;
  loading: boolean;
  error: string | null;
  resuming: boolean;
  onResume: (runId: string) => void;
}

/**
 * Render progress and audit metadata for the selected run.
 *
 * @param props - Selected job and detail request state
 * @returns Selected job detail panel
 */
export function JobDetails({
  job,
  loading,
  error,
  resuming,
  onResume,
}: JobDetailsProps) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5">
      <h2 className="mb-4 text-lg font-semibold">Job details</h2>
      {loading && !job ? (
        <p className="text-sm text-gray-500">Loading run details…</p>
      ) : !job ? (
        <p className="text-sm text-gray-500">
          Select a past job to inspect its status and timing.
        </p>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-between">
            <JobStatusBadge status={job.status} />
            {["queued", "running"].includes(job.status) && (
              <span className="text-xs text-amber-700">
                Refreshing every 4 seconds
              </span>
            )}
          </div>
          <ProgressBar job={job} />
          <dl className="text-sm">
            <DetailRow label="Run ID" value={job.run_id} mono />
            <DetailRow label="Job" value={job.job_name} />
            <DetailRow label="Source" value={formatJobSource(job.source)} />
            <DetailRow
              label="Started"
              value={formatJobTimestamp(job.started_at)}
            />
            <DetailRow
              label="Completed"
              value={formatJobTimestamp(job.completed_at)}
            />
            <DetailRow
              label="Duration"
              value={formatJobDuration(job.started_at, job.completed_at)}
            />
            <DetailRow
              label="Processed"
              value={`${job.processed_count} / ${job.total || "—"}`}
            />
            <DetailRow label="Failed" value={String(job.failed_count)} />
            {job.source_path && (
              <DetailRow label="Source CSV" value={job.source_path} mono />
            )}
            {job.error_message && (
              <DetailRow
                label="What happened"
                value={formatJobError(job.error_message)}
                danger
              />
            )}
          </dl>
          {job.can_resume && (
            <button
              type="button"
              disabled={resuming}
              onClick={() => onResume(job.run_id)}
              className="mt-4 rounded-md bg-orange-600 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resuming ? "Resuming…" : "Resume unfinished MRNs"}
            </button>
          )}
          <JobOutcomeSummary job={job} />
          <JobBatchDetails batches={job.batches} />
        </>
      )}
      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
    </section>
  );
}

/**
 * Render completed-patient progress for one job.
 *
 * @param props.job - Selected ingestion job
 * @returns Progress bar, or null before the denominator is known
 */
function ProgressBar({ job }: { job: JobStatusResponse }) {
  if (!job.total) return null;
  const percent = Math.min(
    100,
    Math.round((job.processed_count / job.total) * 100),
  );
  const color =
    job.status === "failed"
      ? "#dc2626"
      : job.status === "success"
        ? "#059669"
        : job.status === "interrupted"
          ? "#ea580c"
          : "#d97706";
  return (
    <div className="mb-5">
      <div className="mb-1 flex justify-between text-xs text-gray-500">
        <span>
          {job.processed_count} / {job.total} patients
        </span>
        <span>{percent}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${percent}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

/**
 * Render one job metadata row.
 *
 * @param props - Detail label, value, and optional styling
 * @returns Description-list row
 */
function DetailRow({
  label,
  value,
  mono,
  danger,
}: {
  label: string;
  value: string;
  mono?: boolean;
  danger?: boolean;
}) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-2 border-b border-gray-100 py-2 last:border-0">
      <dt className="text-gray-500">{label}</dt>
      <dd
        className={`${mono ? "break-all font-mono text-xs" : "break-words"} ${
          danger ? "text-red-700" : ""
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
